import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Brain, Plus, Edit, Trash2, Save, X, Home, ArrowLeft } from 'lucide-react';
import ErnieProBadge from '@/components/ErnieProBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'general', label: '📋 General', color: 'bg-gray-500' },
  { value: 'personal', label: '👤 Personal', color: 'bg-blue-500' },
  { value: 'preferences', label: '⚙️ Preferences', color: 'bg-purple-500' },
  { value: 'work', label: '💼 Work', color: 'bg-green-500' },
  { value: 'health', label: '🏥 Health', color: 'bg-red-500' },
  { value: 'other', label: '📌 Other', color: 'bg-yellow-500' }
];

export default function ErnieMemoriesPage() {
  const navigate = useNavigate();
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMemory, setEditingMemory] = useState(null);
  const [formData, setFormData] = useState({ content: '', category: 'general' });
  const [proStatus, setProStatus] = useState(null);

  useEffect(() => {
    checkProStatus();
    loadMemories();
  }, []);

  const checkProStatus = async () => {
    try {
      const response = await axios.get(`${API}/ernie-pro/status`, { withCredentials: true });
      setProStatus(response.data);
      if (!response.data.has_pro) {
        toast.error('Memory feature requires Ernie AI Pro');
        navigate('/ernie-ai');
      }
    } catch (error) {
      console.error('Failed to check Pro status:', error);
      navigate('/ernie-ai');
    }
  };

  const loadMemories = async () => {
    try {
      const response = await axios.get(`${API}/ernie-ai/memories`, { withCredentials: true });
      setMemories(response.data.memories || []);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Memory feature requires Ernie AI Pro');
        navigate('/ernie-ai');
      } else {
        toast.error('Failed to load memories');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.content.trim()) {
      toast.error('Please enter memory content');
      return;
    }

    try {
      if (editingMemory) {
        await axios.put(
          `${API}/ernie-ai/memories/${editingMemory.id}`,
          formData,
          { withCredentials: true }
        );
        toast.success('Memory updated');
      } else {
        await axios.post(
          `${API}/ernie-ai/memories`,
          formData,
          { withCredentials: true }
        );
        toast.success('Memory saved');
      }
      
      setShowAddModal(false);
      setEditingMemory(null);
      setFormData({ content: '', category: 'general' });
      loadMemories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save memory');
    }
  };

  const handleEdit = (memory) => {
    setEditingMemory(memory);
    setFormData({ content: memory.content, category: memory.category });
    setShowAddModal(true);
  };

  const handleDelete = async (memoryId) => {
    if (!window.confirm('Delete this memory?')) return;

    try {
      await axios.delete(`${API}/ernie-ai/memories/${memoryId}`, { withCredentials: true });
      toast.success('Memory deleted');
      loadMemories();
    } catch (error) {
      toast.error('Failed to delete memory');
    }
  };

  const getCategoryInfo = (category) => {
    return CATEGORIES.find(c => c.value === category) || CATEGORIES[0];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#2C1810]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E8934D]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#2C1810]">
      {/* Header */}
      <div className="bg-[#3D2817] border-b-4 border-[#E85D4D] px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/ernie-ai" className="flex items-center gap-2 text-[#F4E4C1] hover:text-[#E8934D] transition-colors font-bold">
            <ArrowLeft className="w-5 h-5" />
            Back to Chat
          </Link>
          
          <div className="flex items-center gap-3">
            <Brain className="w-8 h-8 text-[#E8934D]" />
            <h1 className="text-3xl retro-title text-[#F4E4C1]">My Memories</h1>
            {proStatus?.has_pro && <ErnieProBadge />}
          </div>
          
          <Link to="/" className="flex items-center gap-2 text-[#F4E4C1] hover:text-[#E8934D] transition-colors font-bold">
            <Home className="w-5 h-5" />
            Home
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto p-6">
        {/* Info Banner */}
        <div className="mb-6 p-4 bg-purple-900/30 border-2 border-purple-500 rounded-lg">
          <p className="text-purple-100">
            💡 <strong>Memory System:</strong> Tell Ernie to remember things about you, and I'll use this information to personalize our conversations. 
            You can also manage memories here directly.
          </p>
        </div>

        {/* Add Memory Button */}
        <button
          onClick={() => {
            setEditingMemory(null);
            setFormData({ content: '', category: 'general' });
            setShowAddModal(true);
          }}
          className="retro-button w-full flex items-center justify-center gap-2 mb-6 bg-green-600 hover:bg-green-700"
        >
          <Plus className="w-5 h-5" />
          Add New Memory
        </button>

        {/* Memory Count */}
        <div className="mb-4 text-[#F4E4C1]">
          <span className="font-bold">{memories.length}</span> memories saved
          {memories.length >= 20 && (
            <span className="ml-2 text-yellow-400">(Limit reached)</span>
          )}
        </div>

        {/* Memories Grid */}
        {memories.length === 0 ? (
          <div className="text-center py-12">
            <Brain className="w-16 h-16 mx-auto mb-4 text-[#E8934D] opacity-50" />
            <p className="text-[#F4E4C1] text-lg">No memories yet</p>
            <p className="text-[#E8B67A] text-sm mt-2">
              Start by adding a memory or tell Ernie "Remember: [fact]" in the chat!
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {memories.map((memory) => {
              const categoryInfo = getCategoryInfo(memory.category);
              const createdDate = new Date(memory.created_at);
              
              return (
                <div key={memory.id} className="cassette-card p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`${categoryInfo.color} text-white px-2 py-1 rounded text-xs font-bold`}>
                          {categoryInfo.label}
                        </span>
                        <span className="text-xs text-gray-500">
                          {createdDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          {' at '}
                          {createdDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-[#2C1810] whitespace-pre-wrap">{memory.content}</p>
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(memory)}
                        className="p-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(memory.id)}
                        className="p-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4">
          <div className="bg-white max-w-2xl w-full rounded-lg shadow-2xl">
            <div className="bg-gradient-to-r from-purple-600 to-purple-500 text-white p-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Brain className="w-6 h-6" />
                {editingMemory ? 'Edit Memory' : 'Add New Memory'}
              </h2>
              <button onClick={() => setShowAddModal(false)} className="hover:text-gray-200">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 text-gray-900 font-medium bg-white"
                  style={{ color: '#1F2937' }}
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value} style={{ color: '#1F2937' }}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Memory Content
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  rows="6"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 resize-vertical text-gray-900 font-medium"
                  placeholder="e.g., I'm a TCM student at NYU, graduating in 2026"
                  style={{ color: '#1F2937' }}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleSave}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-500 text-white font-bold rounded-lg hover:shadow-lg transition-all flex items-center justify-center gap-2"
                >
                  <Save className="w-5 h-5" />
                  {editingMemory ? 'Update Memory' : 'Save Memory'}
                </button>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-6 py-3 bg-gray-200 text-gray-800 font-bold rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
